import React, { useMemo } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import TrashIcon from '@assets/images/icons/trashcan.svg'
import GrabIcon from '@assets/images/icons/grab.svg'
import { SquareIconButton } from '@/modules/base/components/buttons'
import { SignatureItem } from '@/modules/signature/types'
import ThemeInterface from '@/theme-interface'
import { SIGNER_COLOR_CODES } from '@/redux/ducks/signature/edit'
import { getContrastText } from '@/modules/base/helpers/getContrastText'
import { getFieldTypeInfo } from '@/modules/signature/_helpers/getFieldTypeInfo'
import TextEllipsis from '@/modules/base/components/texts/text-ellipsis'

const defaultBackgroundColor = SIGNER_COLOR_CODES[0]
type SignatureItemProps = {
  index: number,
  item: SignatureItem,
  active: boolean,
  onClick: (index: number, item: SignatureItem) => void,
  onDelete: (index: number, item: SignatureItem) => void,
}

interface SignaturePlacementItemStyleProps {
  signatureColor: string
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    fontSize: theme.fontSizes.small,
    marginBottom: 10,
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  content: ({ signatureColor }: SignaturePlacementItemStyleProps) => ({
    display: 'flex',
    flex: 1,
    minWidth: 0,
    padding: '15px 20px',
    backgroundColor: signatureColor,
    border: `1px solid ${theme.borderColors.default}`,
    borderRadius: 4,
    color: getContrastText(signatureColor),
    cursor: 'pointer',
    height: 45,
  }),
  icon: {
    marginRight: 20,
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    minWidth: 0,
    gap: 10,
  },
  label: {
    flex: 1,
    minWidth: 0,
  },
  page: {
    flexShrink: 0,
  },
  delete: {
    marginLeft: 15,
  },
  active: ({ signatureColor }: SignaturePlacementItemStyleProps) => ({
    '& $content': {
      color: getContrastText(signatureColor),
      borderColor: theme.black,
      backgroundColor: signatureColor,
    },
  }),
}), { name: 'SignaturePlacementItem' })

const SignaturePlacementItem = (props: SignatureItemProps) => {
  const { index, item, active, onClick, onDelete } = props
  const { position, color: signatureColor = defaultBackgroundColor, type, description } = item
  const classes = useStyles({ signatureColor })

  const rootClass = clsx(classes.root, active && classes.active)
  const icon = useMemo(() => {
    if (active) { return GrabIcon }
    return getFieldTypeInfo(item).icon
  }, [active, type])
  const label = useMemo(() => {
    return getFieldTypeInfo(item).label
  }, [type, description])

  return (
    <div className={rootClass}>
      <div className={classes.content} onClick={() => onClick && onClick(index, item)}>
        <div className={classes.icon}>
          <SVGIcon
            svg={icon}
            height="13px"
            width="13px"
            fill={getContrastText(signatureColor)} />
        </div>
        <div className={classes.info}>
          <TextEllipsis className={classes.label}>{label}</TextEllipsis>
          <div className={classes.page}>{lang.signature.page} {position.page}</div>
        </div>
      </div>
      <div className={classes.delete}>
        <SquareIconButton
          size="large"
          icon={TrashIcon}
          onClick={() => onDelete && onDelete(index, item)} />
      </div>
    </div>
  )
}

export default SignaturePlacementItem
